import React, { useState, useMemo } from 'react';
import usePlants from '../hooks/usePlants';
import './PlantForm.css';

function PlantForm({ plant, onSubmit, onCancel, speciesData }) {
  const { searchSpecies } = usePlants();

  const [formData, setFormData] = useState({
    source: plant?.source || '',
    qtyPlugs: plant?.qtyPlugs || 0,
    qtySoloCups: plant?.qtySoloCups || 0,
    qtyQuarts: plant?.qtyQuarts || 0,
    qtyGallons: plant?.qtyGallons || 0,
    trayNumber: plant?.trayNumber || '',
  });

  // Species switch state
  const [speciesQuery, setSpeciesQuery] = useState('');
  const [showSpeciesSearch, setShowSpeciesSearch] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [switchedSpecies, setSwitchedSpecies] = useState(null);

  const suggestions = useMemo(() => {
    if (!speciesQuery.trim()) return [];
    return searchSpecies(speciesQuery).slice(0, 8);
  }, [speciesQuery, searchSpecies]);

  const handleSelectSpecies = (sp) => {
    setSwitchedSpecies(sp);
    setShowSpeciesSearch(false);
    setShowSuggestions(false);
    setSpeciesQuery('');
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field, value) => {
    const num = parseInt(value, 10);
    setFormData((prev) => ({ ...prev, [field]: isNaN(num) ? 0 : Math.max(0, num) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const updates = { ...formData };
    // If species was switched, include the species-level fields
    if (switchedSpecies) {
      updates.speciesId = switchedSpecies.id;
      updates.commonName = switchedSpecies.commonName;
      updates.scientificName = switchedSpecies.scientificName;
      updates.agcpRating = switchedSpecies.agcpRating;
      updates.empRating = switchedSpecies.empRating;
      updates.lightRequirements = switchedSpecies.lightRequirements || '';
      updates.moistureRequirements = switchedSpecies.moistureRequirements || '';
      updates.soilRequirements = switchedSpecies.soilRequirements || '';
      updates.bloomSeason = switchedSpecies.bloomSeason || '';
      updates.bloomColor = switchedSpecies.bloomColor || '';
      updates.saltTolerance = switchedSpecies.saltTolerance || null;
    }
    onSubmit(updates);
  };

  // Display values (use switched species if selected, then speciesData prop, then plant instance)
  const displaySpecies = switchedSpecies || speciesData || plant;

  return (
    <form className="plant-form" onSubmit={handleSubmit} noValidate>
      <h2 className="plant-form__title">Edit Instance</h2>

      {/* Species info (read-only display + switch option) */}
      <div className="plant-form__section">
        <h3 className="plant-form__section-title">Species</h3>

        <div className="plant-form__readonly">
          <p><strong>{displaySpecies?.commonName}</strong></p>
          <p className="plant-form__scientific-display">{displaySpecies?.scientificName}</p>
        </div>

        {!showSpeciesSearch ? (
          <button
            type="button"
            className="plant-form__switch-species-btn"
            onClick={() => setShowSpeciesSearch(true)}
          >
            Switch Species
          </button>
        ) : (
          <div className="plant-form__species-search">
            <label htmlFor="species-switch-search" className="plant-form__label">
              Search species catalog
            </label>
            <input
              id="species-switch-search"
              type="text"
              className="plant-form__input"
              value={speciesQuery}
              onChange={(e) => { setSpeciesQuery(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Type common or scientific name..."
              autoComplete="off"
            />
            {showSuggestions && speciesQuery.trim() && (
              <div className="plant-form__suggestions">
                {suggestions.length > 0 ? (
                  <ul>
                    {suggestions.map((sp) => (
                      <li key={sp.id}>
                        <button type="button" onClick={() => handleSelectSpecies(sp)}>
                          <strong>{sp.commonName}</strong> — <em>{sp.scientificName}</em>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="plant-form__no-match">No species found</p>
                )}
              </div>
            )}
            <button
              type="button"
              className="plant-form__cancel-search-btn"
              onClick={() => { setShowSpeciesSearch(false); setSpeciesQuery(''); }}
            >
              Cancel
            </button>
          </div>
        )}

        {switchedSpecies && (
          <p className="plant-form__switch-notice">
            Will switch to: <strong>{switchedSpecies.commonName}</strong>
          </p>
        )}
      </div>

      {/* Read-only species details */}
      <div className="plant-form__section">
        <h3 className="plant-form__section-title">Species Details (read-only)</h3>
        <div className="plant-form__readonly-grid">
          {displaySpecies?.agcpRating && <span>AGCP: {displaySpecies.agcpRating}</span>}
          {displaySpecies?.empRating && <span>EMP: {displaySpecies.empRating}</span>}
          {displaySpecies?.lightRequirements && <span>Light: {displaySpecies.lightRequirements}</span>}
          {displaySpecies?.moistureRequirements && <span>Moisture: {displaySpecies.moistureRequirements}</span>}
          {displaySpecies?.soilRequirements && <span>Soil: {displaySpecies.soilRequirements}</span>}
          {displaySpecies?.bloomSeason && <span>Bloom: {displaySpecies.bloomSeason}</span>}
          {displaySpecies?.bloomColor && <span>Color: {displaySpecies.bloomColor}</span>}
          {displaySpecies?.saltTolerance ? <span>Salt Tolerance: {'●'.repeat(displaySpecies.saltTolerance)}{'○'.repeat(5 - displaySpecies.saltTolerance)}</span> : <span>Salt Tolerance: Not set</span>}
        </div>
      </div>

      {/* Editable fields */}
      <div className="plant-form__section">
        <h3 className="plant-form__section-title">Source</h3>
        <div className="plant-form__field">
          <label htmlFor="source" className="plant-form__label">Source</label>
          <input id="source" type="text" className="plant-form__input" value={formData.source} onChange={(e) => handleChange('source', e.target.value)} />
        </div>
      </div>

      <div className="plant-form__section">
        <h3 className="plant-form__section-title">Quantities</h3>
        <div className="plant-form__field">
          <label htmlFor="qtyPlugs" className="plant-form__label">Plugs</label>
          <input id="qtyPlugs" type="number" className="plant-form__input" value={formData.qtyPlugs} onChange={(e) => handleNumberChange('qtyPlugs', e.target.value)} min="0" />
        </div>
        <div className="plant-form__field">
          <label htmlFor="qtySoloCups" className="plant-form__label">Solo Cups</label>
          <input id="qtySoloCups" type="number" className="plant-form__input" value={formData.qtySoloCups} onChange={(e) => handleNumberChange('qtySoloCups', e.target.value)} min="0" />
        </div>
        <div className="plant-form__field">
          <label htmlFor="qtyQuarts" className="plant-form__label">Quarts</label>
          <input id="qtyQuarts" type="number" className="plant-form__input" value={formData.qtyQuarts} onChange={(e) => handleNumberChange('qtyQuarts', e.target.value)} min="0" />
        </div>
        <div className="plant-form__field">
          <label htmlFor="qtyGallons" className="plant-form__label">Gallons</label>
          <input id="qtyGallons" type="number" className="plant-form__input" value={formData.qtyGallons} onChange={(e) => handleNumberChange('qtyGallons', e.target.value)} min="0" />
        </div>
      </div>

      <div className="plant-form__section">
        <h3 className="plant-form__section-title">Tray</h3>
        <div className="plant-form__field">
          <label htmlFor="trayNumber" className="plant-form__label">Tray Number</label>
          <input id="trayNumber" type="text" className="plant-form__input" value={formData.trayNumber} onChange={(e) => handleChange('trayNumber', e.target.value)} placeholder="e.g. T1, Mixed-A" />
        </div>
      </div>

      <div className="plant-form__actions">
        <button type="submit" className="plant-form__btn plant-form__btn--submit">Update</button>
        <button type="button" className="plant-form__btn plant-form__btn--cancel" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export default PlantForm;
